import {
    Body1,
    ElevatedCard,
    Flex,
    IconSearch,
    SearchInput,
    Stack,
} from '@phx/design-system';
import Fuse from 'fuse.js';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import translations from '../../../../public/help-center-locales/en/help-center.json';
import helpCenterRoutes from '../../index';

import classes from './search-input.module.css';

type SearchResult = {
    key: string;
    content: string;
};

export const HelpCenterSearchInput = () => {
    const { t } = useTranslation(['help-center-locales/help-center']);

    const [value, setValue] = useState<string>('');
    const [results, setResults] = useState<SearchResult[]>([]);
    const navigate = useNavigate();

    const faqSearchData = useMemo(() => {
        const data: SearchResult[] = [];
        const faqSection = translations.faq;

        if (faqSection) {
            Object.entries(faqSection).forEach(([key, faqSubSection]) => {
                if (Array.isArray(faqSubSection)) {
                    faqSubSection.forEach((item, index) => {
                        if (item?.question) {
                            data.push({
                                key: `faq.${key}[${index}].question`,
                                content: item.question,
                            });
                        }
                    });
                }
            });
        }

        return data;
    }, [translations]);

    const fuse = useMemo(
        () =>
            new Fuse(faqSearchData, {
                keys: ['content'],
            }),
        [faqSearchData]
    );

    const handleInputChange = useCallback(
        (input: string) => {
            setValue(input);

            if (fuse && input) {
                const searchResults = fuse
                    .search(input)
                    .map((result) => result.item);
                setResults(searchResults);
            } else {
                setResults([]);
            }
        },
        [fuse]
    );

    const handleResultClick = useCallback(
        (key: string) => {
            const matchedRoute = helpCenterRoutes[0].children?.find(
                (route) => route.id && key.includes(route.id)
            );

            if (matchedRoute?.path) {
                navigate(matchedRoute.path, {
                    state: { header: t(`helpCenter.faq.${matchedRoute.id}`) },
                });
            }
        },
        [navigate, t]
    );

    return (
        <Stack gap={0} pos="relative">
            <SearchInput
                value={value}
                onChange={(e) => handleInputChange(e.target.value)}
                clear={() => handleInputChange('')}
                label={t('helpCenter.searchLabel')}
                autoFocus={false}
            />
            <Stack className={classes.elevatedCard}>
                {results.length > 0 && (
                    <ElevatedCard>
                        <Stack gap="xs" className={classes.searchResults}>
                            {results.map((result, index) => (
                                <Flex
                                    style={{ cursor: 'pointer' }}
                                    gap="xxs"
                                    key={index}
                                    onClick={() =>
                                        handleResultClick(result.key)
                                    }
                                >
                                    <IconSearch color="gray" />
                                    <Body1 noWrap ellipsis>
                                        {result.content}
                                    </Body1>
                                </Flex>
                            ))}
                        </Stack>
                    </ElevatedCard>
                )}
            </Stack>
        </Stack>
    );
};
